import { useMutation } from "@tanstack/react-query"
import { callFirebaseFunction } from "../services/callFirebaseFunction"
import { firebaseAuthUsingToken } from "../services/auth"
import { fetchUserByUid, createUser, updateUser } from "../services/firestore/user"
import {
  AgentPlanEnum,
  AgentProfilePayload,
  CruisePassengerAgeGroup,
  IUser,
  UserPayload,
} from "@cruisen/contract"
import { useNavigate } from "react-router-dom"
import { useStores } from "../models/helpers/useStores"
import { UserModel } from "@cruisen/react-shared"
import { prepareDemoTrip } from "../services/firestore/developerFunctions"
import { save } from "../utils/storage"
import { UserCredential } from "firebase/auth"
import { Analytics } from "../utils/analytics"
import { generateAgentLink } from "../utils/agentLink"
import Config from "../config"
import { getNavigatorStates } from "../components/organisms/Navigator/utils/navigatorStates"
import { getAgentProfile } from "../services/firebase-functions/agentProfile"
import { useState } from "react"

export function useSignIn() {
  const navigate = useNavigate()
  const rootStore = useStores()
  const [isSigningUp, setIsSigningUp] = useState(false)

  const { mutateAsync: mutateLoginWithConfirmationCode, isPending: isCodeLoginLoading } =
    useMutation({
      mutationKey: ["loginWithConfirmationCode"],
      mutationFn: async ({ email, code }: { email: string; code: string }) => {
        const response = await callFirebaseFunction({
          name: "generateAuthLink",
          payload: {
            email,
            confirmationCode: code,
          },
        })

        const result = {
          success: response?.data?.success,
          authLink: response?.data?.result?.authLink || "",
          authToken: response?.data?.result?.authToken || "",
        }

        if (result.success && result.authToken?.length) {
          const authedUser = await firebaseAuthUsingToken(result.authToken)
          const user = await fetchUserByUid(authedUser?.user?.uid || "")

          if (!user) {
            setIsSigningUp(true)
            return
          }

          const agentProfile = await ensureAgentReadiness(user)
          if (agentProfile === null) {
            setIsSigningUp(true)
            return
          }

          const navigatorStates = await getNavigatorStates()
          rootStore.setProp("navigatorStates", navigatorStates)
          save("navigatorStates", navigatorStates)

          rootStore.setProp("loggedInUser", UserModel.create(user))
          Analytics.logLogin()
          navigate("/dashboard")
        }

        return result
      },
    })

  const ensureAgentReadiness = async (user: IUser) => {
    if (!user.agentCode) {
      user.agentCode = user.id
      await updateUser(user.id, {
        agentCode: user.id,
      })
    }

    const agentProfileResult = await getAgentProfile(user.id)
    const agentProfile = agentProfileResult?.agentProfile

    if (!agentProfile) {
      console.log("get agent profile failed", agentProfileResult)
      return null
    }

    rootStore.setProp("agentProfile", agentProfileResult.agentProfile)

    return agentProfile
  }

  const onCodeEntered = async (email: string, code: string) => {
    await mutateLoginWithConfirmationCode({ email, code })
  }

  const createTestData = async (authedUser: UserCredential) => {
    const agentCode = authedUser?.user?.uid

    const newUserPayload: UserPayload = {
      id: authedUser?.user?.uid as string,
      email: authedUser?.user?.email as string,
      firstName: "Advisor",
      lastName: "Testing",
      ageGroup: "adult" as CruisePassengerAgeGroup,
      phoneNumber: "+15875302271",
      avatar: "",
      referralCode: "",
      agentCode,
      isSuperAdmin: false,
    }

    const agentLink = await generateAgentLink(agentCode, newUserPayload)

    const agentProfile: AgentProfilePayload = {
      email: newUserPayload.email,
      phoneNumber: newUserPayload.phoneNumber,
      shortBio: "Passionate travel agent with 10 years of experience in cruise planning.",
      favouriteTravelDestination: "The breathtaking fjords of Norway",
      bestTravelAdvice: "Always pack a sense of adventure and a good book!",
      agencyName: "Sunset Cruises & Travel",
      agencyPhoneNumber: "+14185438090",
      hostAgencyId: null,
      consortiaId: null,
      agentCode,
      plan: AgentPlanEnum.LITE,
      preferredCruiseLineIds: [],
      agentLink,
    }

    const { createdUser, upsertedAgentProfile } = await createUser(newUserPayload, agentProfile)
    if (!createdUser) return { createdUser: null, createdTrip: null }

    const createdTrip = await prepareDemoTrip({ createdUser })

    return { createdUser, createdTrip, createdAgentProfile: upsertedAgentProfile }
  }

  const { mutateAsync: mutateSendLoginLink, isPending } = useMutation({
    mutationFn: async (email: string) => {
      // For easier testing, we save the email in local storage
      save("user", { email })

      const response = await callFirebaseFunction({
        name: "generateAuthLink",
        payload: {
          email: email,
          confirmationCode: "",
          returnLink: Config.environment === "development",
        },
      })

      if (response?.data?.result?.confirmationCode && Config.environment === "development") {
        save("confirmationCode", response?.data?.result?.confirmationCode)
      }

      const result = {
        success: response?.data?.success,
        authLink: response?.data?.result?.authLink || "",
        authToken: response?.data?.result?.authToken || "",
      }

      if (!result.success) {
        console.error("Failed to send login link", result)
        return
      }

      if (!result.authToken) {
        return {
          showCodeInput: true,
        }
      }

      // This part is dev only
      const authedUser = await firebaseAuthUsingToken(result.authToken)
      const user = await fetchUserByUid(authedUser?.user?.uid || "")

      if (!user) {
        const { createdUser, createdTrip, createdAgentProfile } = await createTestData(authedUser)
        if (!createdUser || !createdTrip) return

        rootStore.setProp("activeTrip", createdTrip)
        rootStore.setProp("agentProfile", createdAgentProfile)
        rootStore.setProp("loggedInUser", UserModel.create(createdUser))
      } else {
        const agentProfile = await ensureAgentReadiness(user)
        if (!agentProfile) {
          console.error("agent profile not found")
          return
        }

        rootStore.setProp("loggedInUser", UserModel.create(user))
      }

      const navigatorStates = await getNavigatorStates()
      rootStore.setProp("navigatorStates", navigatorStates)
      save("navigatorStates", navigatorStates)

      navigate("/dashboard")
    },
    mutationKey: ["sendLoginLink"],
  })

  return {
    mutateSendLoginLink,
    onCodeEntered,
    isPending: isPending || isCodeLoginLoading,
    isSigningUp,
  }
}
